import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './ContactAdminPage.css';
import api from '../../api';
import { useSocket } from '../../context/SocketContext';
import { Empty } from 'antd';

const ContactAdminPage = () => {
  const { profile, getUnblockedRoles } = useAuth();
  const navigate = useNavigate();
  const { socket } = useSocket();

  const [unblockedRole, setUnblockedRole] = useState(null);
  const [replies, setReplies] = useState([]);
  const [showRepliesModal, setShowRepliesModal] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [mediaPreview, setMediaPreview] = useState(null);
  const [toggledIssues, setToggledIssues] = useState({});
  const [toggledMedia, setToggledMedia] = useState({});

  // New states for issue validation
  const [showIssueValidation, setShowIssueValidation] = useState(true);
  const [userIssueDescription, setUserIssueDescription] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [issueValidated, setIssueValidated] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  // Add ref for conversation container
  const conversationRef = useRef(null);

  // Function to scroll to latest message
  const scrollToLatest = () => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (profile) {
      const roles = getUnblockedRoles();
      if (roles.length > 0) {
        setUnblockedRole(roles[0]);
      }
    }
  }, [profile, getUnblockedRoles]);

  // Scroll when toggling issue or after sending reply
  useEffect(() => {
    if (Object.values(toggledIssues).some(Boolean)) {
      scrollToLatest();
    }
  }, [toggledIssues, replies]);

  const toggleIssue = (issueId) => {
    setToggledIssues((prev) => {
      if (prev[issueId]) return {}; // Close if already open
      return { [issueId]: true };   // Open only the selected one
    });
  };
  const toggleMediaPreview = (issueId) => {
    setToggledMedia((prev) => ({
      ...prev,
      [issueId]: !prev[issueId],
    }));
  };

  const handleNavigateToRolePage = () => {
    if (unblockedRole === 'user') navigate('/home');
    else if (unblockedRole === 'instructor') navigate('/dashboard');
  };

  // Function to reset validation state
  const resetValidation = () => {
    setShowIssueValidation(true);
    setUserIssueDescription('');
    setIsValidating(false);
    setIssueValidated(false);
    setValidationResult(null);
  };

  // Function to validate issue with ChatGPT
  const validateIssueWithChatGPT = async (issueDescription) => {
    try {
      const response = await fetch(api.openai, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'user',
              content: `Please evaluate if this issue is related to the UlsaApp learning platform and requires admin support. Issue: "${issueDescription}". Respond with only "VALID" if it's a legitimate UlsaApp platform issue that needs admin attention, or "INVALID" if it's not related to the platform or doesn't require admin support.`,
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to validate issue');
      }

      const data = await response.json();
      const aiResponse = data.choices[0].message.content.trim();

      return aiResponse === 'VALID';
    } catch (error) {
      console.error('Error validating issue:', error);
      return false;
    }
  };

  // Function to handle issue validation
  const handleIssueValidation = async () => {
    if (!userIssueDescription.trim()) {
      Swal.fire('Error', 'Please describe your issue before proceeding.', 'error');
      return;
    }

    setIsValidating(true);

    try {
      const isValid = await validateIssueWithChatGPT(userIssueDescription);

      if (isValid) {
        setIssueValidated(true);
        setValidationResult({
          valid: true,
          message: 'Your issue has been validated. You can now proceed to contact support.',
        });
        setShowIssueValidation(false);
        Swal.fire('Issue Validated', 'Your issue is related to UlsaApp platform. You can now contact support.', 'success');
      } else {
        setValidationResult({
          valid: false,
          message: 'This issue doesn\'t appear to be related to the UlsaApp platform or doesn\'t require admin support. Please check our FAQ or help center first.',
        });
        Swal.fire('Issue Not Valid', 'This issue doesn\'t appear to be related to the UlsaApp platform. Please check our FAQ or help center for common issues.', 'warning');
      }
    } catch (error) {
      setValidationResult({
        valid: false,
        message: 'Unable to validate your issue at the moment. Please try again or contact support directly.',
      });
      Swal.fire('Validation Error', 'Unable to validate your issue. Please try again.', 'error');
    } finally {
      setIsValidating(false);
    }
  };

  const handleReportIssue = () => {
    // Only allow reporting if issue is validated
    if (!issueValidated) {
      Swal.fire('Issue Validation Required', 'Please describe and validate your issue first before contacting support.', 'warning');
      setShowIssueValidation(true);
      return;
    }

    Swal.fire({
      title: 'Report the Issue',
      html: `
        <div id="report-issue-form">
          <label for="issue-category">Issue Category:</label>
          <select id="issue-category">
            <option value="Complaint">Complaint</option>
            <option value="Suggestion">Suggestion</option>
            <option value="Question">Question</option>
            <option value="Technical Issue">Technical Issue</option>
          </select>
          <textarea id="issue-description" placeholder="Tell us what happened..." maxlength="100" rows="5" style="width: 90%; padding: 10px; margin-top: 10px; border: 2px solid #ccc; border-radius: 5px;"></textarea>
          <label for="issue-document" style="margin-top: 10px;">Attach any related document (Optional):</label>
          <input type="file" id="issue-document" accept="image/*,video/*" style="margin-top: 10px;" />
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const category = document.getElementById('issue-category')?.value;
        const description = document.getElementById('issue-description')?.value;
        const file = document.getElementById('issue-document')?.files[0];
        if (!category || !description) {
          Swal.showValidationMessage('Please select a category and provide a description.');
          return false;
        }
        return { category, description, file: file || null };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const { category, description, file } = result.value;
        handleSubmitReport(category, description, file);
      }
    });
  };

  const handleSubmitReport = async (category, issueDescription, file) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      Swal.fire('Error', 'You\'re not logged in. Please log in again.', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('userId', profile._id);
    formData.append('name', profile.name);
    formData.append('email', profile.email);
    formData.append('category', category);
    formData.append('description', issueDescription);
    formData.append('notification', true);
    if (file) formData.append('document', file);

    try {
      const response = await axios.post(api.submitBlockedIssue, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      // Emit socket event for new blocked issue
      if (socket) {
        socket.emit('blockedIssueSubmitted', {
          issue: response.data.issue,
          message: 'New blocked issue reported',
        });
      }

      Swal.fire('Thank You!', 'We sincerely apologize for the inconvenience. Your issue has been successfully reported, and our admin team is actively working to resolve it as soon as possible.', 'success');
    } catch (error) {
      Swal.fire('Error', 'Failed to submit your issue.', 'error');
    }
  };

  const fetchReplies = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return Swal.fire('Error', 'Please log in again.', 'error');

    try {
      const res = await axios.get(api.getUserBlockedIssuesWithReplies, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sortedIssues = res.data.issues.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      setReplies(sortedIssues);
      setShowRepliesModal(true);
    } catch (err) {
      Swal.fire('Error', 'Could not fetch replies.', 'error');
    }
  };

  const handleSendUserReply = async (issueId) => {
    if (!replyMessage.trim()) {
      Swal.fire('Error', 'Reply message cannot be empty.', 'error');
      return;
    }

    const token = localStorage.getItem('authToken');
    try {
      await axios.post(
        `${api.userReplyToBlockedIssue}/${issueId}`,
        { message: replyMessage },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setReplyingTo(null);
      setReplyMessage('');
      await fetchReplies();
      // Scroll will happen automatically due to the useEffect
    } catch (err) {
      Swal.fire('Error', 'Failed to send reply.', 'error');
    }
  };

  const handleMediaClick = (url, type) => setMediaPreview({ url, type });
  const closeHeaderButton = () => setShowRepliesModal(false);
  const closeMediaPreview = () => setMediaPreview(null);

  return (
    <div className="contact-admin-container">
      <h1 className="contact-admin-header">Contact for Support</h1>

      {showIssueValidation ? (
        // Issue Validation Form
        <div className="issue-validation-section">
          <div className="validation-header">
            <h3>Describe Your Issue</h3>
            <button
              onClick={() => navigate(-1)}
              className="validation-close-btn"
              title="Go back to previous page"
            >
              ×
            </button>
          </div>

          <p className="contact-admin-text">
            Before contacting support, please describe your issue so we can validate if it requires admin attention.
          </p>

          <div className="validation-form">
            <textarea
              value={userIssueDescription}
              onChange={(e) => setUserIssueDescription(e.target.value)}
              placeholder="Please describe your issue in detail. For example: 'I cannot access my course content', 'Payment was deducted but course not enrolled', 'Video is not playing properly', etc. This will not be saved.
              "
              className="issue-description-input"
              rows="6"
              maxLength="500"
            />
            <div className="character-count">
              {userIssueDescription.length}/500 characters
            </div>

            <button
              onClick={handleIssueValidation}
              disabled={isValidating || !userIssueDescription.trim()}
              className="validate-issue-btn"
            >
              {isValidating ? 'Validating...' : 'Validate Issue'}
            </button>

            {validationResult && (
              <div className={`validation-result ${validationResult.valid ? 'valid' : 'invalid'}`}>
                <p>{validationResult.message}</p>
                {!validationResult.valid && (
                  <button
                    onClick={() => {
                      setShowIssueValidation(false);
                      setIssueValidated(true); // Allow bypass for now
                    }}
                    className="bypass-validation-btn"
                  >
                    Contact Support Anyway
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        // Main Contact Support Interface
        <>
          <p className="contact-admin-text">
            We&apos;re sorry that you&apos;ve been blocked. Please let us know how we can
            help.
          </p>

          {/* Warning about delayed replies */}
          <div className="reply-delay-warning">
            <div className="warning-icon">⚠️</div>
            <div className="warning-content">
              <h4>Response Time Notice</h4>
              <p>
                Due to high volume of support requests, replies may be delayed. We
                typically respond within 24-48 hours. Sorry for the inconvenience 🙏.
              </p>
            </div>
          </div>

          <div className="action-buttons">
            <button onClick={handleReportIssue} className="report-issue-btn">
              Report the Issue
            </button>
            <button onClick={fetchReplies} className="get-replies-btn">
              Check Replies from Admin
            </button>
            <button onClick={resetValidation} className="reset-validation-btn">
              Describe New Issue to validate
            </button>
            {unblockedRole && (
              <button
                className="navigate-role-btn"
                onClick={handleNavigateToRolePage}
              >
                Go to{' '}
                {unblockedRole.charAt(0).toUpperCase() + unblockedRole.slice(1)}{' '}
                Dashboard
              </button>
            )}
          </div>

          {showRepliesModal && (
            <div className="modal-overlay">
              <div className="replies-modal">
                <div className="replies-content">
                  <div className="replies-modal-header">
                    <h2>Support Conversation History</h2>
                    <button className="replies-close-button" onClick={closeHeaderButton}>
                      ×
                    </button>
                  </div>

                  {replies.length === 0 ? (
                    <div className="no-replies-message">
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No support conversations found"
                      />
                    </div>
                  ) : (
                    replies.map((issue) => (
                      <div
                        key={issue._id}
                        className={`issue-container ${toggledIssues[issue._id] ? 'expanded' : ''}`}
                      >
                        <div
                          className="issue-header"
                          onClick={() => toggleIssue(issue._id)}
                        >
                          <div>
                            <h3>{issue.category}</h3>
                            <p className="issue-date">
                              {new Date(issue.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <div className={`toggle-icon ${toggledIssues[issue._id] ? 'rotated' : ''}`}>
                            ▼
                          </div>
                        </div>

                        {toggledIssues[issue._id] && (
                          <>
                            <div className="issue-content">
                              <p className="issue-text">
                                <strong>Issue Description:</strong> {issue.raisedIssues}
                              </p>

                              {issue.document && (
                                <div className="document-toggle-wrapper">
                                  <button
                                    className="toggle-document-btn"
                                    onClick={() => toggleMediaPreview(issue._id)}
                                  >
                                    {toggledMedia[issue._id] ? 'Hide Attachment' : 'View Attachment'}
                                  </button>
                                </div>
                              )}

                              {toggledMedia[issue._id] && issue.document && (
                                <div className="document-preview">
                                  {issue.document.match(/\.(png|jpg|jpeg)$/i) ? (
                                    <img
                                      src={`${api.img}${issue.document}`}
                                      alt="Document"
                                      className="attached-image"
                                      onClick={() => handleMediaClick(`${api.img}${issue.document}`, 'image')}
                                    />
                                  ) : issue.document.match(/\.(mp4|mov|avi)$/i) ? (
                                    <video
                                      controls
                                      className="attached-video"
                                      onClick={() => handleMediaClick(`${api.img}${issue.document}`, 'video')}
                                    >
                                      <source src={`${api.img}${issue.document}`} type="video/mp4" />
                                    </video>
                                  ) : (
                                    <a
                                      href={`${api.img}${issue.document}`}
                                      className="document-link"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      View Document
                                    </a>
                                  )}
                                </div>
                              )}

                              <div className="conversation-container" ref={conversationRef}>
                                {/* Initial issue message */}
                                <div className="message user-message">
                                  <p>{issue.raisedIssues}</p>
                                  <small>
                                    {new Date(issue.createdAt).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </small>
                                </div>

                                {/* Replies */}
                                {issue.replies?.map((reply, index) => (
                                  <div
                                    key={index}
                                    className={`message ${reply.isUserReply ? 'user-message' : 'admin-message'}`}
                                  >
                                    <p>{reply.message}</p>
                                    <small>
                                      {new Date(reply.repliedAt).toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </small>
                                  </div>
                                ))}
                              </div>

                              <div className="chat-input-container">
                                <div className="chat-input-wrapper">
                                  <textarea
                                    value={replyingTo === issue._id ? replyMessage : ''}
                                    onChange={(e) => {
                                      setReplyingTo(issue._id);
                                      setReplyMessage(e.target.value);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (replyMessage.trim()) {
                                          handleSendUserReply(issue._id);
                                        }
                                      }
                                    }}
                                    placeholder="Type your message here..."
                                    className="chat-input"
                                  />
                                  <button
                                    onClick={() => handleSendUserReply(issue._id)}
                                    className="send-button"
                                    disabled={!replyMessage.trim() || replyingTo !== issue._id}
                                  >
                                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {mediaPreview && (
            <div className="media-preview-overlay" onClick={closeMediaPreview}>
              <div className="media-preview-container" onClick={(e) => e.stopPropagation()}>
                <button className="media-close-btn" onClick={closeMediaPreview}>
                  ×
                </button>
                {mediaPreview.type === 'image' ? (
                  <img
                    src={mediaPreview.url}
                    alt="Preview"
                    className="media-preview-content"
                  />
                ) : (
                  <video controls className="media-preview-content">
                    <source src={mediaPreview.url} type="video/mp4" />
                  </video>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContactAdminPage;
